import React from 'react';
import { useSelector } from 'react-redux';
import { TrevorComponent } from '@trevor/ui-components';
import ColumnName from '@/util/ColumnName';
import NewWorldColumn from '@/util/NewWorldColumn';
import AggregateColumn from '@/util/AggregateColumn';
import { DATE, DATETIME } from '@/util/ColumnType';
import DropdownItem from '@/util/DropdownItem';
import MultiDropdown from '@/components/MultiDropdown';
import style from '@/css/module/credentials.css';
import { ADD_DIMENSIONS_BUTTON } from '@/constants/test-ids';
import DropdownButton from '@/components/DropdownButton';
import TableRef from '@/util/TableRef';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  openDimensionsDropdown,
  selectDimensionsDropdownOpened,
} from '@/components/layers/LayerAggregate/layerAggregateSlice';
import { PlusButton } from '@/views/QueryBuilder/subcomponents/PlusButton';
import convertColumnTypeToDisplayName from '@/util/ColumnTypeToDisplayNameConverter';
import { getUniques } from '@/util/helpers/array-helpers';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { getCurrentPks, getIsAnyStepFromSavedQuery } from '@/store-selectors';

export type GroupByDropdownListProps = {
  columns: NewWorldColumn[];
  getTableOrInsightName: F3<string, boolean, boolean, string>;
  groupBys: AggregateColumn[];
  selectGroupBys: F2<ColumnName, Maybe<string>>;
  dateOptions: F<F<string>>;
  metricsCount: number;
  aliases: Record<string, TableRef>;
};

const isPk = (
  columnNameObj: AggregateColumn,
  aliases: Record<string, TableRef>,
  currentPks: { _alias: string; _name: string }[],
  isFromSavedQuery: boolean,
) => {
  if (isFromSavedQuery) {
    return currentPks
      .map((pk) => `${pk._alias}:${pk._name}`)
      .includes(`${columnNameObj._alias}:${columnNameObj._name}`);
  }

  return (
    columnNameObj.name() === 'id' &&
    columnNameObj.alias() &&
    aliases[columnNameObj.alias()]
  );
};

const GroupByDropdownList_LEGACY: TrevorComponent<
  GroupByDropdownListProps & {
    currentPks: { _alias: string; _name: string }[];
    isFromSavedQuery: boolean;
  }
> = ({
  columns,
  groupBys,
  getTableOrInsightName,
  selectGroupBys,
  dateOptions,
  metricsCount,
  aliases,
  currentPks,
  isFromSavedQuery,
}) => {
  const dispatch = useAppDispatch();
  const dimensionsDropdownOpened = useAppSelector(
    selectDimensionsDropdownOpened,
  );

  React.useEffect(() => {
    if (dimensionsDropdownOpened) {
      dispatch(openDimensionsDropdown(false));
    }
  }, [dimensionsDropdownOpened]);

  const uniqueAliases = getUniques(columns, (n: NewWorldColumn) => {
    return n.name().hasAlias() ? n.name().alias() : 'custom column';
  }); // an array of unique aliases
  const chosenTables = groupBys.filter((columnNameObj) =>
    isPk(columnNameObj, aliases, currentPks, isFromSavedQuery),
  );

  const groupByMenu = uniqueAliases
    .map((m: string) => {
      const tableName = getTableOrInsightName(m, true, false);
      // only include columns that are visible, for this alias.
      let relevantColumns = columns
        .filter((c) => {
          // always show id columns, even if hidden
          return c.isPk() ? true : c.isVisible();
        })
        .filter((c) => {
          // Call options with no alias 'custom column'
          const alias = c.name().hasAlias()
            ? c.name().alias()
            : 'custom column';
          return alias === m;
        });

      // only allow aggregating on types that support comparison (e.g. json doesn't)
      relevantColumns = relevantColumns.filter((c) => c.type().operators());

      // don't allow choosing columns from table that has been chosen
      relevantColumns = relevantColumns.filter(
        (c) => !chosenTables.some((pk) => pk.alias() === c.name().alias()),
      );

      // don't include groupby options already added.
      const columnsNotYetSelected = relevantColumns.filter((c) => {
        const alias = c.name().hasAlias() ? c.name().alias() : null;
        const alreadyAdded = groupBys.some(
          (gb) => gb.name() === c.name().name() && gb.alias() === alias,
        );
        return !alreadyAdded;
      });

      // create dropdown items for submenu
      const items = columnsNotYetSelected.map((v) => {
        // for datetime columns, create a dropdown with the aggregation options (day, week, month, year etc.)
        if (v.type().type() === DATETIME || v.type().type() === DATE) {
          const dateTimeOptions = dateOptions((d) =>
            selectGroupBys(v.name(), d),
          );
          return DropdownItem.dropdown(
            <span>
              {v.name().name()}{' '}
              <small className="faint">
                {convertColumnTypeToDisplayName(v.type().type())}
              </small>
            </span>,
            dateTimeOptions,
            false,
            v.name().name(),
          );
        }
        return DropdownItem.action(
          <span>
            {v.name().name()}{' '}
            <small className="faint">
              {convertColumnTypeToDisplayName(v.type().type())}
            </small>
          </span>,
          safeOnClick(() => selectGroupBys(v.name(), null)),
          false,
          v.name().name(),
        );
      });

      // if an id column exists, add suggested 'group by table name' option above the other options

      // get PK column for this table. Should only be one. Can't choose table unless an id column exists
      const isPKCol = columnsNotYetSelected.filter((column) => column.isPk());

      // can't choose a table if one of its columns has already been chosen
      const colAlreadySelected = groupBys.some((gb) => gb.alias() === m);

      if (isPKCol.length > 0 && !colAlreadySelected) {
        const tableNameWithoutType = getTableOrInsightName(m, true, true);
        const suggested = DropdownItem.action(
          <span>
            <b>{tableNameWithoutType}</b>{' '}
            <small className="faint">suggested</small>
          </span>,
          safeOnClick(() => selectGroupBys(isPKCol[0].name(), null)),
        );
        items.unshift(DropdownItem.divider());
        items.unshift(suggested);
      }

      return DropdownItem.dropdownWithSearch(tableName, items);
    })
    .filter(Boolean); // the filter here removes null values (allowing us to check the length to see if it's empty)

  if (groupByMenu.length === 0) {
    return null;
  }
  return (
    <DropdownButton
      open={dimensionsDropdownOpened}
      contentBuilder={() => <MultiDropdown items={groupByMenu} />}
    >
      <PlusButton
        data-test-id={ADD_DIMENSIONS_BUTTON}
        className={
          metricsCount === 1 && groupBys.length === 0 ? style.pulsate : ''
        }
      />
    </DropdownButton>
  );
};
export const GroupByDropdownList: TrevorComponent<GroupByDropdownListProps> = (
  props,
) => {
  const currentPks = useSelector(getCurrentPks);
  const isFromSavedQuery = useSelector(getIsAnyStepFromSavedQuery);

  return (
    <GroupByDropdownList_LEGACY
      {...props}
      currentPks={currentPks}
      isFromSavedQuery={isFromSavedQuery}
    />
  );
};
